import { Anchor, Button, IconArrowUpRight } from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import { useDrawerSubForm } from 'hooks'
import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Item } from 'typings'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from 'ui'
import { AudienceSelector } from './AudienceSelector'
import { messages } from './messages'
import { styles } from './styles'
import { suite } from './validation'

type Props = {
  state: UseDrawerStateReturn
  onSave: (values: Item[]) => void
  initalValues: Item[] | undefined
}

export type AudienceForm = {
  audiences: Item[]
}

export function AudienceEditor(props: Props) {
  const { state, onSave, initalValues } = props
  const { hide } = state

  const intl = useIntl()
  const { $t } = intl

  const config: AudienceForm = useMemo(
    () => ({ audiences: initalValues ?? [] }),
    [initalValues]
  )

  const { drawerForm } = useDrawerSubForm<AudienceForm>({
    config,
    isOpen: state.open,
    validationFn: suite(intl, 'audiences'),
  })

  const { account } = useAdmin()

  const handleSave = async () => {
    const isValid = await drawerForm.trigger()
    if (isValid) {
      const values = drawerForm.getValues('audiences')
      onSave(values)
      hide()
    }
  }

  const navigateToAudiences = () =>
    window.open(`https://${account}.myvtex.com/admin/rnb#/campaigns`, '_blank')

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>
          <FormattedMessage {...messages.title} />
        </DrawerTitle>
        <DrawerSubtitle>
          <FormattedMessage
            {...messages.editor.subtitle}
            values={{
              a: (content) => (
                <Anchor
                  target="_blank"
                  href={$t(messages.editor.moreInformation)}
                >
                  {content}
                </Anchor>
              ),
            }}
          />
        </DrawerSubtitle>
        <DismissButton onHide={hide} />
      </DrawerHeader>
      <DrawerContent>
        <AudienceSelector name="audiences" form={drawerForm} />
        <Button
          onClick={navigateToAudiences}
          variant="neutralTertiary"
          icon={<IconArrowUpRight />}
          iconPosition="end"
          className={styles.nagivateButton}
        >
          <FormattedMessage {...messages.editor.goToAudiences} />
        </Button>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
